import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { logout } from '../../api/admin.api';
import { fetchContactMessagesUnreadCount } from '../../api/contactMessages.api';
import { fetchRecruitmentSummary } from '../../api/recruitment.api';
import { useAdminProfile } from '../../contexts/AdminProfileContext';
import { usePlatformPermissions } from '../../hooks/usePlatformPermissions';
import { ROUTE_VIEW_PERMISSION } from '../../config/platform-permissions';

interface NavItem {
  path: string;
  labelKey: string;
  icon: string;
  end?: boolean;
}

const navGroups: { groupKey: string; items: NavItem[] }[] = [
  {
    groupKey: 'overview',
    items: [{ path: '/dashboard', labelKey: 'nav.dashboard', icon: 'ri-dashboard-3-line' }],
  },
  {
    groupKey: 'business',
    items: [
      { path: '/order-plans', labelKey: 'nav.order_plans', icon: 'ri-shopping-bag-3-line' },
      { path: '/demo-tenants', labelKey: 'nav.demo_tenants', icon: 'ri-flask-line' },
      { path: '/expired-demo-tenants', labelKey: 'nav.expired_demo_tenants', icon: 'ri-timer-flash-line' },
      { path: '/ateliers', labelKey: 'nav.ateliers', icon: 'ri-store-2-line' },
      { path: '/subscriptions', labelKey: 'nav.subscriptions', icon: 'ri-checkbox-circle-line' },
      { path: '/payments', labelKey: 'nav.payments', icon: 'ri-bank-card-line' },
      { path: '/plans', labelKey: 'nav.plans', icon: 'ri-price-tag-3-line' },
      { path: '/payment-gateways', labelKey: 'nav.payment_gateways', icon: 'ri-secure-payment-line' },
      { path: '/messages', labelKey: 'nav.messages', icon: 'ri-mail-unread-line' },
    ],
  },
  {
    groupKey: 'recruitment',
    items: [
      { path: '/recruitment/jobs', labelKey: 'nav.recruitment_jobs', icon: 'ri-briefcase-4-line' },
      { path: '/recruitment/applications', labelKey: 'nav.recruitment_applications', icon: 'ri-user-follow-line' },
      { path: '/recruitment/settings', labelKey: 'nav.recruitment_settings', icon: 'ri-equalizer-line' },
    ],
  },
  {
    groupKey: 'management',
    items: [
      { path: '/users', labelKey: 'nav.users', icon: 'ri-team-line' },
      { path: '/admin-roles', labelKey: 'nav.admin_roles', icon: 'ri-shield-user-line' },
      { path: '/settings', labelKey: 'nav.settings', icon: 'ri-settings-3-line' },
    ],
  },
  {
    groupKey: 'intelligence',
    items: [
      { path: '/intelligence', labelKey: 'nav.intel_command', icon: 'ri-command-line', end: true },
      { path: '/intelligence/conversations', labelKey: 'nav.intel_conversations', icon: 'ri-chat-3-line' },
      { path: '/intelligence/leads', labelKey: 'nav.intel_leads', icon: 'ri-user-star-line' },
      { path: '/intelligence/follow-ups', labelKey: 'nav.intel_followups', icon: 'ri-alarm-line' },
    ],
  },
  {
    groupKey: 'platform',
    items: [
      { path: '/feature-flags', labelKey: 'nav.feature_flags', icon: 'ri-flag-line' },
      { path: '/support', labelKey: 'nav.support', icon: 'ri-customer-service-2-line' },
      { path: '/notifications', labelKey: 'nav.notifications', icon: 'ri-notification-3-line' },
      { path: '/logs', labelKey: 'nav.logs', icon: 'ri-file-list-3-line' },
      { path: '/integrations', labelKey: 'nav.integrations', icon: 'ri-plug-line' },
      { path: '/releases', labelKey: 'nav.releases', icon: 'ri-git-branch-line' },
      { path: '/marketing', labelKey: 'nav.marketing', icon: 'ri-megaphone-line' },
    ],
  },
];

const SIDEBAR_SCROLL_KEY = 'admin-sidebar-scroll';

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { admin } = useAdminProfile();
  const { can } = usePlatformPermissions();
  const [messagesUnread, setMessagesUnread] = useState(0);
  const [applicationsNew, setApplicationsNew] = useState(0);
  const navRef = useRef<HTMLElement>(null);

  const refreshUnread = () => {
    void fetchContactMessagesUnreadCount().then((r) => {
      if (r.ok) setMessagesUnread(r.unread_count);
    });
    if (can('view_recruitment')) {
      void fetchRecruitmentSummary().then((r) => {
        if (r.ok) setApplicationsNew(r.data.new);
      });
    }
  };

  useEffect(() => {
    refreshUnread();
    const timer = window.setInterval(refreshUnread, 30000);
    const onChanged = () => refreshUnread();
    window.addEventListener('contact-messages:changed', onChanged);
    window.addEventListener('focus', onChanged);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('contact-messages:changed', onChanged);
      window.removeEventListener('focus', onChanged);
    };
  }, [can]);

  useLayoutEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const saved = Number(sessionStorage.getItem(SIDEBAR_SCROLL_KEY) || '0');
    if (Number.isFinite(saved) && saved > 0) {
      el.scrollTop = saved;
    }
  }, [location.pathname]);

  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const onScroll = () => {
      sessionStorage.setItem(SIDEBAR_SCROLL_KEY, String(el.scrollTop));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const isActive = (path: string, end?: boolean) => {
    if (end) return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const visibleGroups = useMemo(() => {
    return navGroups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => {
          const perm = ROUTE_VIEW_PERMISSION[item.path];
          return !perm || can(perm);
        }),
      }))
      .filter((group) => group.items.length > 0);
  }, [can]);

  const initials = (admin?.name ?? 'SA')
    .split(/\s+/)
    .map((p) => p[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <aside className="w-64 h-full min-h-0 overflow-hidden bg-gray-900 flex flex-col flex-shrink-0">
      <div className="px-4 py-4 border-b border-gray-800">
        <button
          type="button"
          onClick={() => navigate('/profile')}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg bg-gray-800/60 hover:bg-gray-800 transition-colors cursor-pointer text-start"
        >
          <div className="w-9 h-9 flex items-center justify-center rounded-full bg-teal-600 text-white text-sm font-bold flex-shrink-0 overflow-hidden">
            {admin?.avatar_url ? (
              <img src={admin.avatar_url} alt="" className="w-full h-full object-cover" />
            ) : (
              initials
            )}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white truncate">{admin?.name ?? admin?.role?.name ?? t('admin.role')}</p>
            <p className="text-xs text-gray-400 truncate">{admin?.email ?? '—'}</p>
          </div>
        </button>
      </div>

      <nav ref={navRef} className="flex-1 min-h-0 px-3 py-4 overflow-y-auto">
        {visibleGroups.map((group, groupIdx) => (
          <div key={group.groupKey} className={groupIdx > 0 ? 'mt-6' : ''}>
            <p className="text-xs font-semibold text-gray-500 tracking-wider px-3 mb-2">
              {t(`nav.groups.${group.groupKey}`)}
            </p>
            <ul className="space-y-0.5">
              {group.items.map((item) => {
                const active = isActive(item.path, item.end);
                const badge =
                  item.path === '/messages'
                    ? messagesUnread
                    : item.path === '/recruitment/applications'
                      ? applicationsNew
                      : 0;
                return (
                  <li key={item.path}>
                    <button
                      type="button"
                      onClick={() => navigate(item.path, { preventScrollReset: true })}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                        active
                          ? 'bg-teal-600 text-white'
                          : 'text-gray-400 hover:text-white hover:bg-gray-800'
                      }`}
                    >
                      <div className="relative w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-base`} />
                        {badge > 0 ? (
                          <span className="absolute -top-1.5 -end-1.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-black leading-4 text-center shadow-sm">
                            {badge > 99 ? '99+' : badge}
                          </span>
                        ) : null}
                      </div>
                      <span className="flex-1 text-start">{t(item.labelKey)}</span>
                      {badge > 0 ? (
                        <span className="w-2 h-2 rounded-full bg-rose-500 flex-shrink-0" aria-hidden />
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className="px-4 py-4 border-t border-gray-800">
        <button
          type="button"
          onClick={async () => {
            await logout();
            navigate('/admin/login', { replace: true });
          }}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-gray-800 transition-all cursor-pointer"
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-logout-box-line text-base" />
          </div>
          {t('nav.sign_out')}
        </button>
      </div>
    </aside>
  );
}
